import { Fragment } from 'react';
import type { RateioResponse } from '@triade/contracts';
import { formatarContagem, formatarMoeda } from '@/lib/format';
import { BotaoExpandir, CardTotal, NUMERO, TabelaDestinos, TD_VALOR, TH_VALOR, useExpansao } from './comum';

/** Aba CIFs: cards, lancamentos com destinos expansiveis e rodape com o total da API (nada e somado aqui). */
export function AbaCifs({ rateio }: { rateio: RateioResponse }) {
  const { aberta, alternar } = useExpansao();
  const cifs = rateio.cifs;
  const comValor = cifs.some((c) => c.valor !== undefined);
  const rateadas = cifs.filter((c) => c.bloqueada).length;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-3">
        {rateio.totaisAbas && <CardTotal testid="card-cifs-mes" rotulo="CIFs do mês" valor={formatarMoeda(rateio.totaisAbas.cifs)} tom="sucesso" />}
        <CardTotal testid="card-cifs-lancamentos" rotulo="Lançamentos" valor={formatarContagem(cifs.length)} />
        <CardTotal testid="card-cifs-rateadas" rotulo="Já rateadas" valor={formatarContagem(rateadas)} />
      </div>

      {cifs.length === 0 ? (
        <p role="status" className="px-5 py-8 text-center text-small text-ink-muted">Nenhum lançamento de CIF nesta competência.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full min-w-[560px] border-collapse text-small" aria-label="CIFs rateados">
            <thead>
              <tr className="bg-surface-muted text-left text-caption text-ink-muted">
                <th scope="col" className="w-10 px-2 py-2" aria-label="Expandir" />
                <th scope="col" className={`${TH_VALOR} sticky left-0 z-10 bg-surface-muted`}>Lançamentos</th>
                <th scope="col" className={TH_VALOR}>Rateada?</th>
                {comValor && <th scope="col" className={`${TH_VALOR} sticky right-0 z-10 bg-surface-muted text-right`}>Valor</th>}
              </tr>
            </thead>
            <tbody>
              {cifs.map((c) => {
                const nome = `${c.identificador} · ${c.descricao}`;
                const ab = aberta(c.id);
                return (
                  <Fragment key={c.id}>
                    <tr data-testid="linha-cif-rateio" className="border-t border-line-soft">
                      <td className="px-2 py-2.5 align-top">
                        <BotaoExpandir aberta={ab} rotulo={nome} onClick={() => alternar(c.id)} />
                      </td>
                      <td className={`${TD_VALOR} sticky left-0 z-[1] bg-card`}>
                        <span className="font-semibold">{c.identificador}</span>
                        <span className="text-ink-secondary"> · {c.descricao}</span>
                      </td>
                      <td className={`${TD_VALOR} whitespace-nowrap`}>{c.bloqueada ? 'Sim' : 'Não'}</td>
                      {comValor && <td className={`${TD_VALOR} ${NUMERO} sticky right-0 z-[1] bg-card`}>{formatarMoeda(c.valor)}</td>}
                    </tr>
                    {ab && (
                      <tr className="bg-surface-muted">
                        <td />
                        <td colSpan={comValor ? 3 : 2} className="px-3 pb-4">
                          <TabelaDestinos destinos={c.destinos} rotulo={nome} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
            {comValor && rateio.totaisAbas && (
              <tfoot>
                <tr className="border-t border-border font-semibold">
                  <td />
                  <td className={`${TD_VALOR} sticky left-0 z-[1] bg-surface-muted`}>Total</td>
                  <td />
                  <td className={`${TD_VALOR} ${NUMERO} sticky right-0 z-[1] bg-surface-muted`} data-testid="total-cifs">{formatarMoeda(rateio.totaisAbas.cifs)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}
